import { processStepBadge, type BadgeSpec } from './tree-badges';
import type { OrderTree } from './orders.api';

/** fu-7 order-detail header: the distinct dienst-codes across every product (dienst-chips). */
export function deriveServiceCodes(tree: OrderTree): string[] {
  const codes = new Set(tree.products.flatMap((product) => product.services.map((service) => service.serviceCode)));
  return [...codes].sort();
}

/**
 * fu-7 order-detail header: the voortgang/kwaliteitsbadge. Mirrors the products table's
 * "Processtap" column — the first still-active service (in product/service order) with a
 * defined, in-progress step. Returns null when every service is either complete or has no
 * progress definition; the header falls back to the plain order-status badge in that case.
 */
export function deriveOrderProgressBadge(tree: OrderTree): BadgeSpec | null {
  for (const product of tree.products) {
    for (const service of product.services) {
      if (service.state !== 'active' || service.progress == null) continue;
      const badge = processStepBadge(service);
      if (badge.status === 'process-current') return badge;
    }
  }
  return null;
}

/** fu-7 order-detail header: "ingediend {{date}}" — the earliest known timeline event. */
export function deriveSubmittedAt(events: readonly { timestamp: string }[]): string | null {
  if (events.length === 0) return null;
  return events.reduce((earliest, event) => (event.timestamp < earliest ? event.timestamp : earliest), events[0]!.timestamp);
}

/** fu-7 order-detail header: "doorlooptijd {{duration}}" — span between first and last known event. */
export function deriveLeadTimeMs(events: readonly { timestamp: string }[]): number | null {
  if (events.length === 0) return null;
  const timestamps = events.map((event) => new Date(event.timestamp).getTime());
  return Math.max(...timestamps) - Math.min(...timestamps);
}
